import { useState, useEffect, useCallback } from 'react';
import { lotesVBApi } from '../../lib/api';
import api from '../../lib/api';
import { ocPrimary, ocDisplayInline } from '../../lib/ocFormat';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Search, Loader2, Plus, Package, CheckCircle, AlertTriangle, X,
  Eye, Send, FileText, Trash2, Check, MessageSquare, ClipboardCheck,
  ChevronDown, ChevronUp, ScanLine,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtFecha } from '../../lib/utils'; // Iter 292.g — helper canónico DD-MM-YYYY

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

const fuenteColors = {
  DTE: 'bg-green-100 text-green-700',
  OC: 'bg-blue-100 text-blue-700',
  'OC (proporcional)': 'bg-blue-50 text-blue-600',
  Estimado: 'bg-amber-100 text-amber-700',
};

function FuenteBadge({ fuente }) {
  if (!fuente) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold ${fuenteColors[fuente] || 'bg-gray-100 text-gray-600'}`}
      title={fuente === 'Estimado' ? 'Neto + 19% IVA estimado. Puede editar el total.' : `Monto obtenido de: ${fuente}`}>
      {fuente}
    </span>
  );
}

const estadoColors = {
  pendiente_vb: 'bg-amber-100 text-amber-700',
  aprobado: 'bg-green-100 text-green-700',
  parcial: 'bg-blue-100 text-blue-700',
  enviado_contabilizar: 'bg-indigo-100 text-indigo-700',
  contabilizado: 'bg-emerald-100 text-emerald-800',
};
const estadoLabels = {
  pendiente_vb: 'Pendiente VB',
  aprobado: 'Aprobado',
  parcial: 'Parcial',
  enviado_contabilizar: 'Enviado a Contab.',
  contabilizado: 'Contabilizado',
};

function EstadoBadge({ estado }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${estadoColors[estado] || 'bg-gray-100 text-gray-600'}`}
      data-testid={`badge-${estado}`}>
      {estadoLabels[estado] || estado}
    </span>
  );
}

function VBFacturaBadge({ estado }) {
  if (estado === 'aprobada') return <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-semibold"><CheckCircle className="w-3 h-3" /> Aprobada</span>;
  if (estado === 'observada') return <span className="inline-flex items-center gap-0.5 bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-semibold"><AlertTriangle className="w-3 h-3" /> Observada</span>;
  return <span className="inline-flex items-center gap-0.5 bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded text-[10px] font-medium">Pendiente</span>;
}

// ========== PDF Viewer - inline portal overlay ==========
function PDFViewer({ url, title, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBlobUrl(null);
    setError(null);
    const cleanUrl = url.startsWith('/api/') ? url.slice(4) : url;
    api.get(cleanUrl, { responseType: 'blob', validateStatus: (s) => s < 500 })
      .then(res => {
        if (cancelled) return;
        if (res.status >= 400) {
          setError(`Error ${res.status}: Documento no encontrado`);
          setLoading(false);
          return;
        }
        const ct = res.headers?.['content-type'] || '';
        if (ct.includes('json')) {
          setError('El servidor no devolvio un PDF');
          setLoading(false);
          return;
        }
        const blob = new Blob([res.data], { type: 'application/pdf' });
        setBlobUrl(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Error de conexion al obtener el documento');
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [url]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  // Escape key handler
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', handleEsc, true);
    return () => window.removeEventListener('keydown', handleEsc, true);
  }, [onClose]);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-[900px] w-[90vw] h-[80vh] flex flex-col p-0 gap-0"
        data-testid="pdf-overlay"
        onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader className="px-4 py-3 border-b pr-12 space-y-0">
          <DialogTitle className="text-sm font-semibold">{title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 p-2 min-h-0">
          {loading ? <div className="flex justify-center items-center h-full"><Loader2 className="w-8 h-8 animate-spin" /></div>
            : blobUrl ? <iframe src={blobUrl} className="w-full h-full border-none rounded" title={title} />
            : <div className="text-center pt-16 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm font-medium">{error || 'Documento no disponible'}</p>
              </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

import { OCViewer } from '../../components/OCViewer';
import { useEmpresa } from '../../hooks/useEmpresa';

const FORMAS_PAGO_BASE = [
  { value: 'contado', label: 'Contado' },
  { value: 'credito', label: 'Crédito' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'nota_credito', label: 'Nota de Crédito' },
];
const FORMAS_PAGO_OTRO = [{ value: 'otro', label: 'Otro' }];

// ========== Factura Detail + OC Comparison Dialog ==========
function FacturaDetailDialog({ open, onClose, factura, onConfirm, empresa }) {
  const [ocData, setOcData] = useState(null);
  const [loadingOC, setLoadingOC] = useState(false);
  const [formaPago, setFormaPago] = useState('');
  const [formaPagoOtro, setFormaPagoOtro] = useState('');
  const [pdfView, setPdfView] = useState(null);
  // Recepcion search
  const [recSearch, setRecSearch] = useState('');
  const [recResults, setRecResults] = useState([]);
  const [recSearching, setRecSearching] = useState(false);
  const [recDropOpen, setRecDropOpen] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);
  // Facturas sin OC del proveedor
  const [facturasSinOC, setFacturasSinOC] = useState([]);
  const [loadingFactSinOC, setLoadingFactSinOC] = useState(false);
  const [selectedDTE, setSelectedDTE] = useState(null);
  const [tarjetasPago, setTarjetasPago] = useState([]);
  const FORMAS_PAGO = [
    ...FORMAS_PAGO_BASE,
    ...tarjetasPago.map((t) => ({
      value: t.codigo,
      label: t.etiqueta + (t.ultimos_4 ? ` ****${t.ultimos_4}` : '') + (t.banco ? ` (${t.banco})` : ''),
    })),
    ...FORMAS_PAGO_OTRO,
  ];

  useEffect(() => {
    api.get('/tarjetas-pago', { params: { incluir_inactivas: false } })
      .then((r) => setTarjetasPago(r.data || []))
      .catch(() => setTarjetasPago([]));
  }, []);

  useEffect(() => {
    if (open && factura?.numero_oc) {
      setLoadingOC(true);
      setOcData(null);
      api.get(`/compras/orden-compra/${factura.numero_oc}/detalle-completo`)
        .then(res => setOcData(res.data))
        .catch(() => { })
        .finally(() => setLoadingOC(false));
    }
    // Fetch facturas sin OC del proveedor
    if (open && factura?.proveedor_rut) {
      setLoadingFactSinOC(true);
      api.get(`/compras/lotes-vb/facturas-sin-oc?rut_proveedor=${encodeURIComponent(factura.proveedor_rut)}`)
        .then(res => setFacturasSinOC(res.data.facturas_sin_oc || []))
        .catch(() => setFacturasSinOC([]))
        .finally(() => setLoadingFactSinOC(false));
    }
    if (open) {
      setFormaPago('');
      setFormaPagoOtro('');
      setRecSearch('');
      setRecResults([]);
      setSelectedRec(null);
      setRecDropOpen(false);
      setSelectedDTE(null);
    }
  }, [open, factura]);

  // Recepcion autocomplete search
  useEffect(() => {
    if (!recSearch || recSearch.length < 2) { setRecResults([]); return; }
    const timer = setTimeout(async () => {
      setRecSearching(true);
      try {
        const searchTerm = recSearch || factura?.numero_oc || '';
        const res = await api.get(`/compras/recepciones?q=${encodeURIComponent(searchTerm)}&limit=15`);
        setRecResults(res.data.recepciones || []);
        setRecDropOpen(true);
      } catch (e) { setRecResults([]); }
      finally { setRecSearching(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [recSearch, factura]);

  if (!factura) return null;

  const oc = ocData?.oc || {};
  const factTotal = selectedDTE?.monto_total || factura.total;
  const diff = factTotal && oc.total ? Math.abs(factTotal - oc.total) : null;
  const match = diff !== null && diff < 20;

  const handleConfirm = () => {
    const fp = formaPago === 'otro' ? formaPagoOtro : FORMAS_PAGO.find(f => f.value === formaPago)?.label || '';
    const recNum = selectedRec?.numero_recepcion || factura.numero_recepcion || '';
    const result = { ...factura, forma_pago_real: fp || undefined, numero_recepcion: recNum };
    // If a DTE was selected from "Facturas sin OC", enrich the result
    if (selectedDTE) {
      result.numero_factura = selectedDTE.folio;
      result.numero_dte = selectedDTE.folio;
      result.medio_pago_dte = selectedDTE.medio_pago || '';
      if (selectedDTE.monto_total) {
        result.total = selectedDTE.monto_total;
        result.monto_neto = selectedDTE.monto_neto || 0;
        result.iva = (selectedDTE.monto_total || 0) - (selectedDTE.monto_neto || 0);
        result.fuente_monto = 'DTE';
      }
    }
    onConfirm(result);
  };

  const openOCPdf = () => setPdfView({ url: `/api/compras/oc/${factura.numero_oc}/pdf`, title: `OC ${factura.numero_oc}` });
  const openRecPdf = () => {
    const recNum = selectedRec?.numero_recepcion || factura.numero_recepcion;
    if (recNum) setPdfView({ url: `/api/compras/recepciones/${recNum}/pdf`, title: `Recepción ${recNum}` });
  };
  const openScan = async () => {
    toast.info('Abriendo escaneo...');
    const factNum = selectedDTE?.folio || factura.numero_factura;
    const ocNum = factura.numero_oc || '';
    const recNum = selectedRec?.numero_recepcion || factura.numero_recepcion || '';
    if (!factNum) { toast.error('Sin número de factura para buscar scan'); return; }
    try {
      const params = new URLSearchParams();
      if (ocNum) params.set('oc', ocNum);
      if (recNum) params.set('recepcion', recNum);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/contabilidad/sharepoint-scan/${factNum}${qs}`, { responseType: 'blob', validateStatus: s => s < 500 });
      if (res.status >= 400) { toast.error('Scan no disponible'); return; }
      const blob = new Blob([res.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch { toast.error('Error al obtener scan'); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto p-0 gap-0" data-testid="factura-detail-dialog"
          onInteractOutside={(e) => { if (pdfView) e.preventDefault(); }}>

          {/* Header */}
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2 text-base">
              <FileText className="w-5 h-5 text-blue-600" />
              Detalle Factura vs Orden de Compra
            </DialogTitle>
            <DialogDescription>Compare montos y seleccione la forma de pago real antes de agregar al lote</DialogDescription>
          </DialogHeader>

          <div className="px-5 py-4 space-y-4">

            {/* === Comparison Grid: Factura | OC === */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

              {/* LEFT: Factura/DTE Info */}
              <Card className="border-purple-200">
                <CardHeader className="py-2.5 px-4 bg-purple-50/70 border-b border-purple-100">
                  <CardTitle className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Factura / DTE
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <span className="text-muted-foreground">N° Factura</span>
                      <p className="font-bold text-sm">{selectedDTE?.folio || factura.numero_factura || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">N° DTE</span>
                      <p className="font-medium">{selectedDTE?.folio || factura.numero_dte || 'Sin DTE'}</p>
                    </div>
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Proveedor</span>
                      <p className="font-semibold">{factura.proveedor_nombre}</p>
                      <p className="text-[10px] text-muted-foreground">{factura.proveedor_rut}</p>
                    </div>
                  </div>

                  {selectedDTE && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-2 flex items-center justify-between">
                      <div className="text-xs">
                        <span className="text-green-700 font-semibold">DTE seleccionado: </span>
                        <span className="font-bold">{selectedDTE.folio}</span>
                        <span className="ml-2 text-muted-foreground">${fmt(selectedDTE.monto_total)}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-6 text-xs text-red-600 hover:text-red-800 hover:bg-red-50"
                        onClick={() => setSelectedDTE(null)}>
                        <X className="w-3 h-3 mr-1" /> Quitar
                      </Button>
                    </div>
                  )}

                  {/* Amounts */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Neto</span>
                      <span className="font-mono">${fmt(factura.monto_neto)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">IVA</span>
                      <span className="font-mono">${fmt(factura.iva)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold border-t pt-1.5">
                      <span>Total Factura</span>
                      <span className="text-purple-700 font-mono">${fmt(factura.total)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[10px] text-muted-foreground">Fuente:</span>
                      <FuenteBadge fuente={factura.fuente_monto} />
                    </div>
                  </div>

                  {/* DTE payment reference */}
                  {factura.medio_pago_dte && (
                    <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                      <span className="text-amber-700 font-medium">Medio Pago DTE (ref):</span>
                      <span className="ml-1">{factura.medio_pago_dte}</span>
                    </div>
                  )}

                  {/* Document buttons */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(selectedRec?.numero_recepcion || factura.numero_recepcion) && (
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={openRecPdf} data-testid="btn-ver-recepcion">
                        <Eye className="w-3 h-3 mr-1" /> Recepción
                      </Button>
                    )}
                    {factura.scan_recepcion_url && (
                      <Button variant="outline" size="sm" className="text-xs h-7 text-amber-700 border-amber-300" onClick={openScan} data-testid="btn-ver-scan">
                        <ScanLine className="w-3 h-3 mr-1" /> Scan
                      </Button>
                    )}
                  </div>

                  {/* Recepcion search / associate */}
                  <div className="border-t pt-3 mt-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium">Recepción Asociada</span>
                      {(selectedRec || factura.numero_recepcion) && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                          {selectedRec?.numero_recepcion || factura.numero_recepcion}
                        </span>
                      )}
                    </div>
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                      <Input
                        data-testid="rec-search-input"
                        placeholder="Buscar recepción por N°, OC o factura..."
                        className="h-8 text-xs pl-7"
                        value={recSearch}
                        onChange={e => { setRecSearch(e.target.value); setRecDropOpen(true); }}
                        onFocus={() => recResults.length > 0 && setRecDropOpen(true)}
                      />
                      {recSearching && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                      {recDropOpen && recResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto" data-testid="rec-dropdown">
                          {recResults.map(r => (
                            <button key={r.numero_recepcion}
                              className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 border-b last:border-0 flex justify-between items-center"
                              onClick={() => {
                                setSelectedRec(r);
                                setRecSearch(r.numero_recepcion);
                                setRecDropOpen(false);
                              }}>
                              <div>
                                <span className="font-semibold text-blue-700">{r.numero_recepcion}</span>
                                <span className="text-muted-foreground ml-2">OC: {r.numero_oc || '-'}</span>
                              </div>
                              <span className="text-muted-foreground text-[10px]">{r.fecha ? new Date(r.fecha).toLocaleDateString('es-CL') : ''}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {!factura.numero_recepcion && !selectedRec && (
                      <p className="text-[10px] text-muted-foreground italic">Sin recepción — busque por N° OC o factura para asociar</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* RIGHT: OC Detail */}
              <Card className="border-blue-200">
                <CardHeader className="py-2.5 px-4 bg-blue-50/70 border-b border-blue-100">
                  <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <Package className="w-4 h-4" /> Orden de Compra {ocData?.oc ? ocDisplayInline(ocData.oc) : (factura.numero_oc || 'N/A')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {!factura.numero_oc ? (
                    <div className="p-6 text-center text-muted-foreground text-sm">
                      <Package className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      Sin OC asociada
                    </div>
                  ) : loadingOC ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                  ) : ocData ? (
                    <div className="max-h-[400px] overflow-y-auto">
                      <OCViewer
                        oc={ocData.oc}
                        detalle={ocData.detalle || []}
                        proveedor={ocData.proveedor || {}}
                        operaciones={ocData.operaciones || []}
                        formaPago={ocData.forma_pago || ''}
                        esTarjeta={ocData.es_tarjeta || false}
                        empresa={empresa}
                      />
                    </div>
                  ) : (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No se pudo cargar la OC
                    </div>
                  )}
                  {factura.numero_oc && (
                    <div className="px-3 py-2 border-t flex gap-1.5">
                      <Button variant="outline" size="sm" className="text-xs h-7" onClick={openOCPdf} data-testid="btn-ver-oc-pdf">
                        <Eye className="w-3 h-3 mr-1" /> PDF OC
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* === Amount Comparison Banner === */}
            {factura.numero_oc && ocData?.oc && (
              <div className={`rounded-lg p-3 border flex items-center justify-between ${
                match ? 'bg-green-50 border-green-300' : 'bg-amber-50 border-amber-300'
              }`} data-testid="amount-comparison">
                <div className="flex items-center gap-3">
                  {match ? <CheckCircle className="w-5 h-5 text-green-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
                  <div>
                    <p className={`text-sm font-semibold ${match ? 'text-green-800' : 'text-amber-800'}`}>
                      {match ? (diff > 0 ? `Montos coinciden (dif. $${fmt(diff)} < $20)` : 'Montos coinciden') : 'Diferencia en montos'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Factura: <strong>${fmt(factTotal)}</strong> — OC: <strong>${fmt(oc.total)}</strong>
                      {match && diff > 0 && <span className="text-green-600 ml-1">(se usa monto factura)</span>}
                      {diff !== null && !match && <span className="text-red-600 font-semibold ml-1">(Dif: ${fmt(diff)})</span>}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* === Facturas del Proveedor sin OC asignada === */}
            {factura.proveedor_rut && (
              <Card className="border-orange-200" data-testid="facturas-sin-oc-section">
                <CardHeader className="py-2.5 px-4 bg-orange-50/70 border-b border-orange-100">
                  <CardTitle className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Otras Facturas de este Proveedor sin OC
                    <span className="text-[10px] font-normal text-muted-foreground ml-1">
                      ({factura.proveedor_nombre || factura.proveedor_rut})
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3">
                  {loadingFactSinOC ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                    </div>
                  ) : facturasSinOC.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic py-2 text-center">
                      No hay otras facturas/DTEs de este proveedor sin OC asignada
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border rounded">
                      <table className="w-full text-xs">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="px-2 py-1.5 text-left font-semibold">DTE/Folio</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Tipo</th>
                            <th className="px-2 py-1.5 text-right font-semibold">Monto Total</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Medio Pago</th>
                            <th className="px-2 py-1.5 text-left font-semibold">Fecha</th>
                            <th className="px-2 py-1.5 text-center font-semibold">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {facturasSinOC.map((f, i) => {
                            const isSelected = selectedDTE?.folio === f.folio;
                            return (
                            <tr key={i} className={`border-t ${isSelected ? 'bg-green-50 ring-1 ring-green-300' : 'hover:bg-orange-50/50'}`}>
                              <td className="px-2 py-1.5 font-semibold text-orange-700">{f.folio}</td>
                              <td className="px-2 py-1.5 text-center">
                                {f.tipo_dte === '61' ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-100 text-red-700 border border-red-200">NC</span>
                                ) : f.tipo_dte === '56' ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-200">ND</span>
                                ) : f.tipo_dte === '52' ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-gray-100 text-gray-500 border border-gray-200">GD</span>
                                ) : (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-blue-50 text-blue-600">{f.tipo_dte || 'Fact'}</span>
                                )}
                              </td>
                              <td className={`px-2 py-1.5 text-right font-mono ${f.tipo_dte === '61' ? 'text-red-600 font-bold' : ''}`}>
                                {f.tipo_dte === '61' ? '-' : ''}${fmt(f.monto_total)}
                              </td>
                              <td className="px-2 py-1.5 text-muted-foreground">{f.medio_pago || '-'}</td>
                              <td className="px-2 py-1.5 text-muted-foreground text-[10px]">{fmtFecha(f.fecha_emision) || '-'}</td>
                              <td className="px-2 py-1.5 text-center">
                                {isSelected ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-green-100 text-green-700">
                                    <Check className="w-3 h-3 mr-0.5" /> Asociado
                                  </span>
                                ) : (
                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-orange-700 hover:text-orange-900 hover:bg-orange-100 px-2"
                                    data-testid={`asociar-dte-${i}`}
                                    onClick={() => setSelectedDTE(f)}>
                                    <Plus className="w-3 h-3 mr-0.5" /> Asociar
                                  </Button>
                                )}
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* === Payment Method Selector === */}
            <Card className="border-emerald-200">
              <CardHeader className="py-2.5 px-4 bg-emerald-50/70 border-b border-emerald-100">
                <CardTitle className="text-sm font-semibold text-emerald-800">
                  Forma de Pago Real
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-3">
                  Seleccione cómo se pagará realmente esta factura (independiente de lo que indica la DTE o la OC)
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {FORMAS_PAGO.map(fp => (
                    <button
                      key={fp.value}
                      data-testid={`fp-${fp.value}`}
                      onClick={() => setFormaPago(fp.value)}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                        formaPago === fp.value
                          ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                          : 'bg-white text-foreground border-gray-200 hover:border-emerald-400 hover:bg-emerald-50'
                      }`}
                    >
                      {fp.label}
                    </button>
                  ))}
                </div>
                {formaPago === 'otro' && (
                  <Input
                    className="mt-2"
                    placeholder="Especifique forma de pago..."
                    value={formaPagoOtro}
                    onChange={e => setFormaPagoOtro(e.target.value)}
                    data-testid="fp-otro-input"
                  />
                )}
                {/* Reference info */}
                <div className="mt-3 flex gap-4 text-[10px] text-muted-foreground">
                  {factura.medio_pago_dte && <span>Ref. DTE: <strong>{factura.medio_pago_dte}</strong></span>}
                  {ocData?.forma_pago && <span>Ref. OC: <strong>{ocData.forma_pago}</strong></span>}
                  {factura.forma_pago_alegra && <span>Ref. Alegra: <strong>{factura.forma_pago_alegra}</strong></span>}
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="px-5 py-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleConfirm} data-testid="confirmar-agregar-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus className="w-4 h-4 mr-1" /> Confirmar y Agregar al Lote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nested PDF Viewer */}
      {pdfView && <PDFViewer url={pdfView.url} title={pdfView.title} onClose={() => setPdfView(null)} />}
    </>
  );
}

// ========== Create Lote Dialog ==========
function CrearLoteDialog({ open, onClose, onCreated, editLoteId = null }) {
  const empresa = useEmpresa();
  const [nombre, setNombre] = useState('');
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState([]);
  const [creating, setCreating] = useState(false);
  const [detailFactura, setDetailFactura] = useState(null);

  // Recepcion lookup
  const [recepcionNum, setRecepcionNum] = useState('');
  const [lookupResult, setLookupResult] = useState(null);
  const [lookingUp, setLookingUp] = useState(false);

  const isEditMode = !!editLoteId;

  const doSearch = async () => {
    if (!search || search.length < 2) return;
    setSearching(true);
    try {
      // En modo edición, pasamos editLoteId para que NO oculte las facturas del lote actual
      const res = await lotesVBApi.buscarFacturas(search, editLoteId);
      setSearchResults(res.data?.facturas || []);
    } catch { toast.error('Error buscando facturas'); }
    setSearching(false);
  };

  // Editable total for lookup result
  const [editTotal, setEditTotal] = useState(null);

  const doLookupRecepcion = async () => {
    if (!recepcionNum.trim()) return;
    setLookingUp(true);
    setLookupResult(null);
    setEditTotal(null);
    try {
      const res = await lotesVBApi.lookupRecepcion(recepcionNum.trim());
      setLookupResult(res.data);
      setEditTotal(null); // use original total by default
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Recepción no encontrada');
    }
    setLookingUp(false);
  };

  const addFromSearch = (factura) => {
    if (selected.find(s => s.numero_recepcion === factura.numero_recepcion && s.numero_recepcion)) {
      toast.info('Recepción ya agregada');
      return;
    }
    // Open detail dialog instead of adding directly
    setDetailFactura(factura);
  };

  const confirmFromDetail = (facturaWithPago) => {
    if (selected.find(s => s.numero_recepcion === facturaWithPago.numero_recepcion && s.numero_recepcion)) {
      toast.info('Recepción ya agregada');
      setDetailFactura(null);
      return;
    }
    setSelected(prev => [...prev, { ...facturaWithPago }]);
    setDetailFactura(null);
    setSearch('');
    setSearchResults([]);
    toast.success('Factura agregada al lote');
  };

  const addFromLookup = () => {
    if (!lookupResult) return;
    if (selected.find(s => s.numero_recepcion === lookupResult.numero_recepcion)) {
      toast.info('Recepción ya agregada');
      return;
    }
    const item = { ...lookupResult };
    // Apply edited total if user changed it
    if (editTotal !== null && editTotal !== '') {
      const newTotal = parseFloat(editTotal);
      if (!isNaN(newTotal) && newTotal > 0) {
        item.total = newTotal;
        item.iva = newTotal - (item.monto_neto || 0);
        item.fuente_monto = 'Manual';
      }
    }
    setSelected(prev => [...prev, item]);
    setLookupResult(null);
    setRecepcionNum('');
    setEditTotal(null);
    toast.success('Factura agregada al lote');
  };

  const removeSelected = (idx) => setSelected(prev => prev.filter((_, i) => i !== idx));

  const handleCreate = async () => {
    if (selected.length === 0) { toast.error('Agregue al menos una factura'); return; }
    if (!isEditMode && !nombre.trim()) { toast.error('Ingrese un nombre para el lote'); return; }
    setCreating(true);
    try {
      let res;
      if (isEditMode) {
        res = await lotesVBApi.agregarFacturas(editLoteId, selected);
        toast.success(res.data?.message || `${selected.length} facturas agregadas al lote`);
      } else {
        res = await lotesVBApi.crear({ nombre: nombre.trim(), facturas: selected });
        toast.success(res.data?.message || 'Lote creado');
      }
      onCreated?.();
      onClose();
      setNombre(''); setSelected([]); setSearch(''); setSearchResults([]);
      setLookupResult(null); setRecepcionNum('');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEditMode ? 'Error al agregar facturas' : 'Error al crear lote'));
    }
    setCreating(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Agregar facturas al lote' : 'Crear Lote VB'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Buscá y agregá facturas al lote existente. Las facturas que ya están en otros lotes pendientes se filtran automáticamente.'
              : 'Busque y agregue facturas para crear un lote de aprobación'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!isEditMode && (
            <div>
              <label className="text-sm font-medium">Nombre del Lote</label>
              <Input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Lote Marzo Semana 4" data-testid="lote-nombre-input" />
            </div>
          )}

          {/* === PRIMARY: Buscar por OC, factura o proveedor === */}
          <Card className="border-blue-200 bg-blue-50/30">
            <CardContent className="p-3 space-y-2">
              <label className="text-sm font-semibold text-blue-800">Buscar por N° OC, Factura o Proveedor</label>
              <div className="flex gap-2">
                <Input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="N° OC, factura, nombre proveedor, RUT..."
                  onKeyDown={e => e.key === 'Enter' && doSearch()}
                  data-testid="search-facturas-input" className="bg-white" />
                <Button onClick={doSearch} disabled={searching} size="sm" data-testid="search-facturas-btn">
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-1">Buscar</span>
                </Button>
              </div>
              {searchResults.length > 0 && (
                <div className="border rounded bg-white max-h-64 overflow-y-auto">
                  {searchResults.map((f, i) => (
                    <div key={i} className={`flex items-center justify-between px-3 py-2 border-b last:border-0 hover:bg-muted/50 text-sm ${f.sin_recepcion ? 'bg-amber-50/60 border-l-4 border-l-amber-400' : ''}`}>
                      <div className="min-w-0 flex-1">
                        {f.sin_recepcion ? (
                          <>
                            <span className="font-semibold text-amber-700">OC {f.numero_oc}</span>
                            <span className="text-muted-foreground"> — {f.proveedor_nombre || f.proveedor_rut}</span>
                            <span className="text-muted-foreground ml-2">${fmt(f.total)}</span>
                            <FuenteBadge fuente={f.fuente_monto} />
                            <div className="text-xs mt-0.5">
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-red-100 text-red-700 mr-1">Sin Recepción</span>
                              {!f.numero_factura && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-orange-100 text-orange-700">Sin DTE</span>}
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="font-medium">Fact. {f.numero_factura || '?'}</span>
                            <span className="text-muted-foreground"> - {f.proveedor_nombre}</span>
                            <span className="text-muted-foreground ml-2">${fmt(f.total)}</span>
                            <FuenteBadge fuente={f.fuente_monto} />
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {f.numero_oc && <span className="text-blue-600 mr-2">OC: {f.numero_oc}</span>}
                              {f.numero_recepcion && <span className="text-green-600 mr-2">Rec: {f.numero_recepcion}</span>}
                              {f.numero_dte && <span className="text-purple-600 mr-2">DTE: {f.numero_dte}</span>}
                              {f.scan_recepcion_url && <span className="text-amber-600">Scan</span>}
                            </div>
                          </>
                        )}
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => addFromSearch(f)} data-testid={`add-factura-${i}`}
                        title={f.sin_recepcion ? 'Agregar OC al lote (abrir detalle para asociar recepción/DTE)' : 'Agregar al lote'}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* === SECONDARY: Lookup by Recepción === */}
          <details className="border rounded">
            <summary className="px-3 py-2 text-sm font-medium cursor-pointer hover:bg-muted/50">
              Buscar por N° Recepción (detalle completo)
            </summary>
            <div className="px-3 pb-3 pt-2 space-y-2">
              <div className="flex gap-2">
                <Input value={recepcionNum} onChange={e => setRecepcionNum(e.target.value)}
                  placeholder="Ej: 2026013192, D20260322001..."
                  onKeyDown={e => e.key === 'Enter' && doLookupRecepcion()}
                  data-testid="lookup-recepcion-input" />
                <Button onClick={doLookupRecepcion} disabled={lookingUp} size="sm" data-testid="lookup-recepcion-btn">
                  {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  <span className="ml-1">Buscar</span>
                </Button>
              </div>

              {lookupResult && (
                <div className="border rounded bg-white p-3 space-y-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-semibold">{lookupResult.proveedor_nombre || 'Sin proveedor'}</div>
                      <div className="text-xs text-muted-foreground">{lookupResult.proveedor_rut}</div>
                    </div>
                    <div className="text-right space-y-1">
                      <div className="flex items-center gap-1.5 justify-end">
                        <FuenteBadge fuente={lookupResult.fuente_monto} />
                      </div>
                      <div className="text-[10px] text-muted-foreground">Neto: ${fmt(lookupResult.monto_neto)}</div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">Total:</span>
                        <span className="text-xs font-medium">$</span>
                        <Input
                          type="number"
                          className="h-7 w-28 text-right font-bold text-sm"
                          value={editTotal !== null ? editTotal : Math.round(lookupResult.total || 0)}
                          onChange={e => setEditTotal(e.target.value)}
                          data-testid="edit-total-input"
                        />
                      </div>
                      {lookupResult.fuente_monto === 'Estimado' && (
                        <div className="text-[9px] text-amber-600">IVA estimado 19%. Verifique el total.</div>
                      )}
                    </div>
                  </div>

                  {/* Document links */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className={`rounded p-1.5 text-center ${lookupResult.numero_factura ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                      <div className="font-semibold">Factura</div>
                      <div>{lookupResult.numero_factura || 'N/A'}</div>
                    </div>
                    <div className={`rounded p-1.5 text-center ${lookupResult.documentos?.tiene_oc ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-400'}`}>
                      <div className="font-semibold">OC</div>
                      <div>{lookupResult.numero_oc || 'N/A'}</div>
                    </div>
                    <div className={`rounded p-1.5 text-center ${lookupResult.documentos?.tiene_dte ? 'bg-purple-50 text-purple-700' : 'bg-amber-50 text-amber-600'}`}>
                      <div className="font-semibold">DTE</div>
                      <div>{lookupResult.numero_dte || 'Sin DTE'}</div>
                      {lookupResult.documentos?.tiene_dte && <div className="text-[9px]">Disponible para Alegra</div>}
                    </div>
                    <div className={`rounded p-1.5 text-center ${lookupResult.documentos?.tiene_scan ? 'bg-amber-50 text-amber-700' : lookupResult.documentos?.tiene_pdf_recepcion ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                      <div className="font-semibold">Scan</div>
                      <div>{lookupResult.documentos?.tiene_scan ? 'SharePoint' : lookupResult.documentos?.tiene_pdf_recepcion ? 'PDF Local' : 'N/A'}</div>
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground">
                    Recepción: <span className="font-medium">{lookupResult.numero_recepcion}</span>
                    {lookupResult.ot_numero > 0 && <span className="ml-2">| OT: {lookupResult.ot_numero}</span>}
                    {lookupResult.fecha_recepcion && <span className="ml-2">| Fecha: {lookupResult.fecha_recepcion}</span>}
                  </div>

                  <Button onClick={addFromLookup} size="sm" className="w-full" data-testid="add-from-lookup-btn">
                    <Plus className="w-4 h-4 mr-1" /> Agregar al Lote
                  </Button>
                </div>
              )}
            </div>
          </details>

          {/* Selected facturas */}
          {selected.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">{selected.length} factura(s) en el lote</label>
                <div className="text-xs space-x-3">
                  <span className="text-muted-foreground">Neto: <strong>${fmt(selected.reduce((s, f) => s + (f.monto_neto || 0), 0))}</strong></span>
                  <span className="text-muted-foreground">IVA: <strong>${fmt(selected.reduce((s, f) => s + (f.iva || (f.total || 0) - (f.monto_neto || 0)), 0))}</strong></span>
                  <span className="font-semibold">Total: ${fmt(selected.reduce((s, f) => s + (f.total || 0), 0))}</span>
                </div>
              </div>
              <div className="border rounded max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-2 py-1 text-left">Factura</th>
                      <th className="px-2 py-1 text-left">Proveedor</th>
                      <th className="px-2 py-1 text-left">OC</th>
                      <th className="px-2 py-1 text-left">Recepción</th>
                      <th className="px-2 py-1 text-center">DTE</th>
                      <th className="px-2 py-1 text-left">F. Pago Real</th>
                      <th className="px-2 py-1 text-right">Neto</th>
                      <th className="px-2 py-1 text-right">IVA</th>
                      <th className="px-2 py-1 text-right">Total</th>
                      <th className="px-2 py-1 text-center">Fuente</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.map((f, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1 font-medium">{f.numero_factura || '-'}</td>
                        <td className="px-2 py-1 truncate max-w-[150px]">{f.proveedor_nombre}</td>
                        <td className="px-2 py-1 text-blue-600">{f.numero_oc || '-'}</td>
                        <td className="px-2 py-1 text-green-600">{f.numero_recepcion || '-'}</td>
                        <td className="px-2 py-1 text-center">
                          {f.numero_dte ? <CheckCircle className="w-3 h-3 text-purple-600 inline" title={`DTE ${f.numero_dte}`} /> : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1">
                          {f.forma_pago_real ? (
                            <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded text-[10px] font-semibold">{f.forma_pago_real}</span>
                          ) : (
                            <span className="text-muted-foreground text-[10px]">{f.condicion_pago || f.medio_pago_dte || '-'}</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-right font-mono text-muted-foreground text-[10px]">${fmt(f.monto_neto)}</td>
                        <td className="px-2 py-1 text-right font-mono text-muted-foreground text-[10px]">${fmt(f.iva || (f.total - (f.monto_neto || 0)))}</td>
                        <td className="px-2 py-1 text-right font-mono font-medium">${fmt(f.total)}</td>
                        <td className="px-2 py-1 text-center"><FuenteBadge fuente={f.fuente_monto} /></td>
                        <td className="px-2 py-1">
                          <Button size="sm" variant="ghost" onClick={() => removeSelected(i)} className="text-destructive h-5 w-5 p-0">
                            <X className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleCreate} disabled={creating || selected.length === 0} data-testid="crear-lote-submit">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Package className="w-4 h-4 mr-1" />}
            Crear Lote ({selected.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Factura Detail + OC Comparison Dialog */}
    <FacturaDetailDialog
      open={!!detailFactura}
      onClose={() => setDetailFactura(null)}
      factura={detailFactura}
      onConfirm={confirmFromDetail}
      empresa={empresa}
    />
    </>
  );
}

// ========== VB Dialog (Gerencia Review) ==========
function VBDialog({ open, onClose, lote, onVBDone }) {
  const [acciones, setAcciones] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lote?.facturas) {
      const init = {};
      lote.facturas.forEach(f => {
        init[f.factura_id] = { accion: f.estado_vb !== 'pendiente' ? f.estado_vb : '', observacion: f.observacion_gerencia || '' };
      });
      setAcciones(init);
    }
  }, [lote]);

  const setAccion = (facturaId, accion) => {
    setAcciones(prev => ({ ...prev, [facturaId]: { ...prev[facturaId], accion } }));
  };
  const setObservacion = (facturaId, obs) => {
    setAcciones(prev => ({ ...prev, [facturaId]: { ...prev[facturaId], observacion: obs } }));
  };

  const selectAll = (accion) => {
    const updated = {};
    lote.facturas.forEach(f => { updated[f.factura_id] = { ...acciones[f.factura_id], accion }; });
    setAcciones(updated);
  };

  const handleSave = async () => {
    const accionesList = Object.entries(acciones)
      .filter(([, v]) => v.accion)
      .map(([facturaId, v]) => ({
        factura_id: facturaId,
        accion: v.accion,
        observacion: v.observacion || '',
      }));
    if (accionesList.length === 0) { toast.error('Seleccione al menos una acción'); return; }
    setSaving(true);
    try {
      const res = await lotesVBApi.darVB(lote.lote_id, accionesList);
      toast.success(res.data?.message || 'VB procesado');
      onVBDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al dar VB');
    }
    setSaving(false);
  };

  const [pdfView, setPdfView] = useState(null); // {url, title}

  const openOCPdf = (oc) => {
    if (oc) setPdfView({ url: `/api/compras/oc/${oc}/pdf`, title: `OC ${oc}` });
  };
  const openRecepcionPdf = (rec) => {
    if (rec) setPdfView({ url: `/api/compras/recepciones/${rec}/pdf`, title: `Recepción ${rec}` });
  };

  if (!lote) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" />
              VB Gerencia - {lote.nombre}
            </DialogTitle>
            <DialogDescription>Revise y apruebe/observe cada factura del lote</DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={() => selectAll('aprobada')} data-testid="select-all-aprobar">
              <Check className="w-3 h-3 mr-1" /> Aprobar Todas
            </Button>
            <Button size="sm" variant="outline" onClick={() => selectAll('observada')} className="text-amber-600" data-testid="select-all-observar">
              <MessageSquare className="w-3 h-3 mr-1" /> Observar Todas
            </Button>
          </div>

          <div className="border rounded">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-2 py-2 text-left">N° Factura</th>
                  <th className="px-2 py-2 text-left">Proveedor</th>
                  <th className="px-2 py-2 text-left">OC</th>
                  <th className="px-2 py-2 text-left">DTE</th>
                  <th className="px-2 py-2 text-left">Recepción</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2 text-center">Docs</th>
                  <th className="px-2 py-2 text-center">Acción</th>
                  <th className="px-2 py-2 text-left">Observación</th>
                </tr>
              </thead>
              <tbody>
                {lote.facturas?.map((f) => {
                  const a = acciones[f.factura_id] || {};
                  return (
                    <tr key={f.factura_id} className={`border-t ${a.accion === 'aprobada' ? 'bg-green-50/50' : a.accion === 'observada' ? 'bg-red-50/50' : ''}`}>
                      <td className="px-2 py-1.5 font-medium">{f.numero_factura}</td>
                      <td className="px-2 py-1.5 text-xs">{f.proveedor_nombre}</td>
                      <td className="px-2 py-1.5">
                        {f.numero_oc ? (
                          <button onClick={() => openOCPdf(f.numero_oc)} className="text-blue-600 hover:underline text-xs" data-testid={`ver-oc-${f.factura_id}`}>
                            {f.numero_oc}
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{f.numero_dte || '-'}</td>
                      <td className="px-2 py-1.5">
                        {f.numero_recepcion ? (
                          <button onClick={() => openRecepcionPdf(f.numero_recepcion)} className="text-blue-600 hover:underline text-xs" data-testid={`ver-rec-${f.factura_id}`}>
                            {f.numero_recepcion}
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono text-xs">${fmt(f.total)}</td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex gap-0.5 justify-center">
                          {f.numero_oc && <FileText className="w-3 h-3 text-blue-500 cursor-pointer" onClick={() => openOCPdf(f.numero_oc)} title="Ver OC" />}
                          {f.numero_recepcion && <ScanLine className="w-3 h-3 text-green-500 cursor-pointer" onClick={() => openRecepcionPdf(f.numero_recepcion)} title="Ver Recepción" />}
                          {f.scan_recepcion_url && <ScanLine className="w-3 h-3 text-purple-500" title="Scan disponible" />}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <div className="flex gap-1 justify-center">
                          <button
                            onClick={() => setAccion(f.factura_id, 'aprobada')}
                            className={`p-1 rounded ${a.accion === 'aprobada' ? 'bg-green-500 text-white' : 'bg-muted hover:bg-green-100'}`}
                            data-testid={`aprobar-${f.factura_id}`}
                            title="Aprobar"
                          ><Check className="w-3.5 h-3.5" /></button>
                          <button
                            onClick={() => setAccion(f.factura_id, 'observada')}
                            className={`p-1 rounded ${a.accion === 'observada' ? 'bg-red-500 text-white' : 'bg-muted hover:bg-red-100'}`}
                            data-testid={`observar-${f.factura_id}`}
                            title="Observar"
                          ><MessageSquare className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        {(a.accion === 'observada' || f.estado_vb === 'observada') && (
                          <Input
                            value={a.observacion || ''}
                            onChange={e => setObservacion(f.factura_id, e.target.value)}
                            placeholder="Observación..."
                            className="h-7 text-xs"
                            data-testid={`obs-${f.factura_id}`}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="guardar-vb-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
              Guardar VB
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {pdfView && <PDFViewer url={pdfView.url} title={pdfView.title} onClose={() => setPdfView(null)} />}
    </>
  );
}

// ========== Lote Detail Dialog ==========
function LoteDetailDialog({ open, onClose, lote, onRefresh }) {
  const [sending, setSending] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const handleEnviarContabilizar = async () => {
    setSending(true);
    try {
      const res = await lotesVBApi.enviarContabilizar(lote.lote_id);
      toast.success(res.data?.message || 'Enviado a contabilizar');
      onRefresh?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar');
    }
    setSending(false);
  };

  if (!lote) return null;

  const aprobadas = lote.facturas?.filter(f => f.estado_vb === 'aprobada').length || 0;
  const observadas = lote.facturas?.filter(f => f.estado_vb === 'observada').length || 0;
  const pendientes = lote.facturas?.filter(f => f.estado_vb === 'pendiente').length || 0;
  const isEditable = lote.estado === 'pendiente_vb';

  const handleRemoveFactura = async (factura) => {
    if (!window.confirm(`¿Quitar la factura ${factura.numero_factura} (${factura.proveedor_nombre}, $${fmt(factura.total)}) del lote?\n\nLa factura volverá al pool de "facturas disponibles" y podrás agregarla a otro lote después.`)) return;
    setRemovingId(factura.factura_id);
    try {
      await lotesVBApi.eliminarFactura(lote.lote_id, factura.factura_id);
      toast.success('Factura eliminada del lote');
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar factura');
    }
    setRemovingId(null);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" /> {lote.nombre} <EstadoBadge estado={lote.estado} />
          </DialogTitle>
          <DialogDescription>Detalle del lote y estado de aprobación</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-muted/50 rounded p-2 text-center">
            <div className="text-lg font-bold">{lote.total_facturas}</div>
            <div className="text-[10px] text-muted-foreground">Total</div>
          </div>
          <div className="bg-green-50 rounded p-2 text-center">
            <div className="text-lg font-bold text-green-700">{aprobadas}</div>
            <div className="text-[10px] text-green-600">Aprobadas</div>
          </div>
          <div className="bg-red-50 rounded p-2 text-center">
            <div className="text-lg font-bold text-red-700">{observadas}</div>
            <div className="text-[10px] text-red-600">Observadas</div>
          </div>
          <div className="bg-amber-50 rounded p-2 text-center">
            <div className="text-lg font-bold text-amber-700">{pendientes}</div>
            <div className="text-[10px] text-amber-600">Pendientes</div>
          </div>
        </div>

        <div className="text-sm text-muted-foreground mb-2">
          Creado por: {lote.creado_por} el {fmtFecha(lote.fecha_creacion)}
          {lote.vb_por && <> | VB: {lote.vb_por} el {fmtFecha(lote.fecha_vb)}</>}
        </div>

        <div className="border rounded">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-2 py-1.5 text-left">N° Factura</th>
                <th className="px-2 py-1.5 text-left">Proveedor</th>
                <th className="px-2 py-1.5 text-left">OC</th>
                <th className="px-2 py-1.5 text-left">Recepción</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-2 py-1.5 text-center">Estado VB</th>
                <th className="px-2 py-1.5 text-left">Observación</th>
                {isEditable && <th className="px-2 py-1.5 text-center w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {lote.facturas?.map((f) => (
                <tr key={f.factura_id} className="border-t">
                  <td className="px-2 py-1.5 font-medium">{f.numero_factura}</td>
                  <td className="px-2 py-1.5 text-xs">{f.proveedor_nombre}</td>
                  <td className="px-2 py-1.5 text-xs">{f.numero_oc || '-'}</td>
                  <td className="px-2 py-1.5 text-xs">{f.numero_recepcion || '-'}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-xs">${fmt(f.total)}</td>
                  <td className="px-2 py-1.5 text-center"><VBFacturaBadge estado={f.estado_vb} /></td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">{f.observacion_gerencia || ''}</td>
                  {isEditable && (
                    <td className="px-2 py-1.5 text-center">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => handleRemoveFactura(f)}
                        disabled={removingId === f.factura_id}
                        title="Quitar factura del lote"
                        data-testid={`remove-factura-${f.factura_id}`}
                      >
                        {removingId === f.factura_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-2">
          {isEditable && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => setShowAddDialog(true)}
              data-testid="btn-agregar-facturas-lote"
            >
              <Plus className="w-4 h-4" /> Agregar más facturas
            </Button>
          )}
          <div className={`text-right font-semibold ${isEditable ? '' : 'ml-auto'}`}>
            Monto Total: ${fmt(lote.total_monto)}
          </div>
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {(lote.estado === 'aprobado' || lote.estado === 'parcial') && aprobadas > 0 && (
            <Button onClick={handleEnviarContabilizar} disabled={sending} data-testid="enviar-contabilizar-btn" className="bg-indigo-600 hover:bg-indigo-700">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar a Contabilizar ({aprobadas})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {/* Sub-dialog: agregar facturas al lote actual */}
    {showAddDialog && (
      <CrearLoteDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onCreated={() => { setShowAddDialog(false); onRefresh?.(); }}
        editLoteId={lote.lote_id}
      />
    )}
    </>
  );
}

// ========== Main Page ==========
export function FirmaVBLote() {
  const empresa = useEmpresa();
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedLote, setSelectedLote] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showVB, setShowVB] = useState(false);
  const [expandedLote, setExpandedLote] = useState(null);

  const loadLotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await lotesVBApi.listar();
      setLotes(res.data?.lotes || []);
    } catch { toast.error('Error cargando lotes'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadLotes(); }, [loadLotes]);

  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDelete = async (loteId) => {
    if (pendingDelete !== loteId) {
      setPendingDelete(loteId);
      setTimeout(() => setPendingDelete(null), 4000); // auto-cancel after 4s
      return;
    }
    setPendingDelete(null);
    try {
      await lotesVBApi.eliminar(loteId);
      toast.success('Lote eliminado');
      loadLotes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const openDetail = async (lote) => {
    try {
      const res = await lotesVBApi.detalle(lote.lote_id);
      setSelectedLote(res.data);
      setShowDetail(true);
    } catch { toast.error('Error cargando detalle'); }
  };

  const openVB = async (lote) => {
    try {
      const res = await lotesVBApi.detalle(lote.lote_id);
      setSelectedLote(res.data);
      setShowVB(true);
    } catch { toast.error('Error cargando lote'); }
  };

  const filteredLotes = lotes.filter(l =>
    !filtro || l.nombre?.toLowerCase().includes(filtro.toLowerCase()) ||
    l.lote_id?.includes(filtro) || l.creado_por?.toLowerCase().includes(filtro.toLowerCase())
  );

  return (
    <div className="p-4 space-y-4" data-testid="firma-vb-lote-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Firma VB por Lote</h1>
          <p className="text-sm text-muted-foreground">Paquetes de facturas para aprobación de Gerencia</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadLotes} variant="outline" size="sm" data-testid="refresh-lotes-btn">
            <Loader2 className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="crear-lote-btn">
            <Plus className="w-4 h-4 mr-1" /> Crear Lote
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Buscar lotes..."
            className="pl-8" data-testid="filter-lotes-input" />
        </div>
      </div>

      {/* Lotes List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : filteredLotes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>No hay lotes creados</p>
          <Button onClick={() => setShowCreate(true)} variant="outline" className="mt-3" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Crear primer lote
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredLotes.map(lote => (
            <Card key={lote.lote_id} className="hover:shadow-md transition-shadow" data-testid={`lote-card-${lote.lote_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Package className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{lote.nombre}</span>
                        <EstadoBadge estado={lote.estado} />
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {lote.total_facturas} factura(s) | ${fmt(lote.total_monto)} | {lote.creado_por} | {fmtFecha(lote.fecha_creacion)}
                        {lote.vb_por && <span className="ml-2 text-green-600">VB: {lote.vb_por}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(lote)} title="Ver detalle" data-testid={`ver-${lote.lote_id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    {(lote.estado === 'pendiente_vb' || lote.estado === 'parcial') && (
                      <Button size="sm" variant="outline" onClick={() => openVB(lote)} title="Dar VB" data-testid={`vb-${lote.lote_id}`}
                        className="text-green-600 border-green-200 hover:bg-green-50">
                        <ClipboardCheck className="w-4 h-4" />
                      </Button>
                    )}
                    {(lote.estado === 'aprobado' || lote.estado === 'parcial') && (
                      <Button size="sm" variant="outline" onClick={() => openDetail(lote)} title="Enviar a contabilizar"
                        className="text-indigo-600 border-indigo-200 hover:bg-indigo-50" data-testid={`send-${lote.lote_id}`}>
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {lote.estado !== 'contabilizado' && (
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(lote.lote_id)} title={pendingDelete === lote.lote_id ? "Click para confirmar" : "Eliminar"}
                        className={pendingDelete === lote.lote_id ? "bg-destructive text-white hover:bg-destructive/90 animate-pulse" : "text-destructive hover:bg-destructive/10"} data-testid={`del-${lote.lote_id}`}>
                        <Trash2 className="w-4 h-4" />
                        {pendingDelete === lote.lote_id && <span className="ml-1 text-xs">Confirmar</span>}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <CrearLoteDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={loadLotes} />
      <LoteDetailDialog open={showDetail} onClose={() => { setShowDetail(false); setSelectedLote(null); }}
        lote={selectedLote} onRefresh={loadLotes} />
      <VBDialog open={showVB} onClose={() => { setShowVB(false); setSelectedLote(null); }}
        lote={selectedLote} onVBDone={loadLotes} />
    </div>
  );
}

export default FirmaVBLote;
